'use client';

import { useEffect } from 'react';

import {
    FILTER,
    FILTER_VAL,
    SHOP_BY_PREFIX,
    TRACK_TYPE_MULTI_ATTR,
} from '@/components/Analytics/constants';
import { GRS_REFINE_QUERY_PARAM } from '@/components/SearchResultsFacets/constants';
import { getKeyValue } from '@/services/content/data';
import { useSearchContext } from '@/services/search/SearchProvider';
import {
    getParam,
    prependCategoryUrl,
    updatePageTitle,
} from '@/services/search/utils';
import { getCategoryAttributes } from '@/src/components/SearchResultsFacets/grs.lib';
import { FacetNormalized } from '@/src/components/SearchResultsFacets/types';
import { GRSFacetConfigProperties } from '@/types/contentStack';
import { checkCurrentCategory, getFilteredCategories } from '@/utils/grs';
import { Box } from '@mui/material';
import { Link, List, Text } from '@nxt-costco-com/forge-components';
import { ColorTextDefault, SpaceXs } from '@nxt-costco-com/forge-design-tokens';
import {
    Analytics,
    type ContentStackEntryDataProps,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';
import { ChevronLeft } from '@nxt-costco-com/forge-icons';

import {
    CHILD_CATEGORY_A11Y_TEXT,
    PARENT_CATEGORY_A11Y_TEXT,
} from './constants';

export function CategoryGRSFilterView({
    categoryUrlPath,
    facet,
    resultsText,
    facetConfig,
    categoryId,
    publishedCategories = [],
    grsFacetConfig,
}: {
    categoryUrlPath: string | undefined;
    facet: FacetNormalized;
    resultsText: string;
    facetConfig: ContentStackEntryDataProps;
    categoryId?: string;
    publishedCategories?: string[];
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
}) {
    const { queryParams: params } = useQueryParams();
    const { searchResult, searchService } = useSearchContext();
    const showMoreText = getKeyValue('facet.showmoretext', facetConfig);

    const refine = getParam(params, GRS_REFINE_QUERY_PARAM);
    const keyword = getParam(params, 'keyword');
    const sortBy = getParam(params, 'sortBy');

    const refineParamValue = refine
        ? `${GRS_REFINE_QUERY_PARAM}=${refine}`
        : '';
    const keywordParamValue = keyword
        ? `keyword=${encodeURIComponent(keyword)}`
        : '';
    const sortByParamValue = sortBy
        ? `sortBy=${encodeURIComponent(sortBy)}`
        : '';

    const combinedParams = [
        refineParamValue,
        keywordParamValue,
        sortByParamValue,
    ]
        .filter(s => !!s)
        .join('&');

    const selectedCategory = facet.buckets?.find(bucket =>
        checkCurrentCategory(bucket, categoryId),
    );

    const selectedCategoryAttributes =
        selectedCategory &&
        getCategoryAttributes(selectedCategory?.val.toString());

    const filteredBuckets = getFilteredCategories(facet, categoryId);

    useEffect(() => {
        if (selectedCategoryAttributes?.label) {
            updatePageTitle({
                searchParams: params,
                grsFacetConfig,
                searchResult,
                searchService,
                pageTitle: selectedCategoryAttributes?.label,
                facetConfig,
            });
        }
    }, [
        params,
        selectedCategoryAttributes?.label,
        grsFacetConfig,
        searchResult,
        searchService,
        facetConfig,
    ]);

    if (!facet.buckets) {
        return null;
    }

    return (
        <>
            <List
                variant="ExpandableList"
                showMoreText={showMoreText}
                ariaLabeledById={facet.facetKey}
                buttonId={`show_more_${facet.label?.toLowerCase()?.split(' ')?.join('_')}`}
                defaultItemsToExpose={10}
            >
                {filteredBuckets.map(bucket => {
                    if (!bucket.val || !bucket.label) {
                        return null;
                    }

                    const isParentCategory = Boolean(
                        bucket.level &&
                        selectedCategoryAttributes?.level &&
                        bucket.level < selectedCategoryAttributes.level,
                    );

                    return (
                        <>
                            {!bucket?.isSelectedCateogry && (
                                <Analytics
                                    key={bucket.val.toString()}
                                    analyticData={{
                                        trackedData: [
                                            {
                                                key: FILTER,
                                                value:
                                                    SHOP_BY_PREFIX +
                                                    facet.label,
                                            },
                                            {
                                                key: FILTER_VAL,
                                                value: bucket.label ?? '',
                                            },
                                        ],
                                        trackType: TRACK_TYPE_MULTI_ATTR,
                                        doNotTrack: bucket.leads_to_fsa_chdi,
                                    }}
                                >
                                    <Box
                                        key={bucket.val?.toString()}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flext-start',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        {/** Category links always route to the .html page with just keyword or sortBy */}
                                        <Link
                                            automation-id={`${facet.facetKey}`}
                                            href={prependCategoryUrl(
                                                categoryUrlPath,
                                                bucket.meta ?? '',
                                                combinedParams,
                                                publishedCategories,
                                                isParentCategory,
                                            )}
                                            underline="always"
                                            aria-label={
                                                isParentCategory
                                                    ? `${bucket.label}, ${getKeyValue(PARENT_CATEGORY_A11Y_TEXT, facetConfig)}`
                                                    : `${bucket.label}, ${getKeyValue(CHILD_CATEGORY_A11Y_TEXT, facetConfig)} ${bucket.count?.toLocaleString()} ${resultsText}`
                                            }
                                            sx={{
                                                color: ColorTextDefault,
                                                textDecoration: 'underline',
                                                display: 'flex',
                                                justifyContent: isParentCategory
                                                    ? 'flex-start'
                                                    : 'space-between',
                                                width: '100%',
                                            }}
                                        >
                                            {isParentCategory && (
                                                <ChevronLeft
                                                    sx={{
                                                        paddingRight: SpaceXs,
                                                        color: ColorTextDefault,
                                                    }}
                                                />
                                            )}
                                            <Box component={'span'}>
                                                {bucket.label}
                                            </Box>
                                            {!isParentCategory && (
                                                <Box
                                                    component={'span'}
                                                    id={`${facet.facetKey}-${bucket.val?.toString()}`}
                                                    sx={{
                                                        color: 'rgb(51, 51, 51)',
                                                    }}
                                                    aria-hidden={true}
                                                >
                                                    (
                                                    {bucket.count?.toLocaleString()}
                                                    )
                                                </Box>
                                            )}
                                        </Link>
                                    </Box>
                                </Analytics>
                            )}
                            {bucket?.isSelectedCateogry && (
                                <Text
                                    variant="t5"
                                    sx={{
                                        color: ColorTextDefault,
                                        paddingLeft: '26px',
                                    }}
                                    aria-current="page"
                                >
                                    {bucket.label}
                                </Text>
                            )}
                        </>
                    );
                })}
            </List>
        </>
    );
}
